import { useState, useEffect } from 'react';
import {
  BarChart3,
  Trophy,
  Settings,
  Gift,
  FlaskConical,
  Calendar,
  Music,
  Check,
  Headphones,
  Building2,
  Users,
  Lock,
  Smartphone,
  ArrowRight,
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import './VersionsPage.css';

function HomePage() {
  const [formData, setFormData] = useState({
    userName: '',
    mobile: '',
    companyName: ''
  });

  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // 订阅版本数据
  const [editions, setEditions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [versionsError, setVersionsError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/rest/subscription_intro/get`)
      .then((resp) => resp.json())
      .then((result) => {
        if (result.code === '0' && result.data?.editions) {
          setEditions(result.data.editions);
        } else {
          setVersionsError(result.msg || '加载失败');
        }
      })
      .catch((err) => {
        console.error('加载订阅版本失败', err);
        setVersionsError('网络异常，请稍后重试');
      })
      .finally(() => setVersionsLoading(false));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const mobileRegex = /^1[3-9]\d{9}$/;
    if (!mobileRegex.test(formData.mobile)) {
      showMessage('请输入正确的中国大陆手机号码', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/add_application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      if (result.code === '0') {
        showMessage('提交成功！我们会尽快与您联系。', 'success');
        setFormData({
          userName: '',
          mobile: '',
          companyName: ''
        });
      } else {
        showMessage('提交失败，请稍后重试。', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('提交失败，请检查网络连接后重试。', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <div className="logo">
            <div className="logo-icon" aria-hidden="true">
              <Music size={18} />
            </div>
            <h1>申嘻教务</h1>
          </div>
          <nav className="navigation">
            <ul>
              <li><a href="#features">核心能力</a></li>
              <li><a href="#music">音乐专属</a></li>
              <li><a href="#cases">客户案例</a></li>
              <li><a href="#versions">订阅版本</a></li>
              <li><a href="#contact">免费试用</a></li>
            </ul>
          </nav>
        </div>
      </header>

      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-pulse"></span>
              专注音乐私教培训 · 已服务多家知名音乐连锁品牌
            </div>
            <h1>
              <span className="gradient-text">为音乐培训</span>
              <br />
              深度定制的教务系统
            </h1>
            <p className="subtitle">
              一对一智能排课、学员约教室练习、多校区管理、跨校区排课、兼职老师适配、多角色授权——通用教务系统做不到的，我们原生支持。每月为您节省300+人天统计量。
            </p>
            <div className="cta-buttons">
              <a href="#contact" className="btn primary">
                试用满意后再签约
                <ArrowRight size={18} />
              </a>
              <a href="#music" className="btn secondary">了解深度适配能力</a>
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-value">300+</span>
                <span className="hero-stat-label">人天/月节省</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-value">2000+</span>
                <span className="hero-stat-label">合作音乐机构</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-value">10年</span>
                <span className="hero-stat-label">专注音乐教培</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features" id="features">
        <div className="container">
          <h2 className="section-title">不只是管课表，更是帮您省钱省力</h2>
          <p className="section-subtitle">
            自动统计、智能排课、工资一键结算——把教务老师从Excel里解放出来
          </p>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-icon-wrap">
                <BarChart3 size={24} strokeWidth={2} />
              </div>
              <h3>每月节省 300+ 人天</h3>
              <p>告别手工Excel统计，教务、财务、课时数据自动汇总。相当于每月少开10个人的工资。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap">
                <Trophy size={24} strokeWidth={2} />
              </div>
              <h3>头部连锁品牌信赖</h3>
              <p>多家知名音乐连锁培训机构的选择。懂行业，更懂您的痛点。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap">
                <Settings size={24} strokeWidth={2} />
              </div>
              <h3>支持定制开发</h3>
              <p>工资结算规则、排课逻辑、审批流程——根据您机构的实际运营方式支持定制开发。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap">
                <Gift size={24} strokeWidth={2} />
              </div>
              <h3>试用满意后再签约</h3>
              <p>全功能开放试用，满意后再付费。零风险体验完整的教务管理闭环。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap">
                <FlaskConical size={24} strokeWidth={2} />
              </div>
              <h3>体验课全流程跟进</h3>
              <p>从预约、上课、跟进到转化，每个环节自动提醒。再也不漏掉任何一个潜在学员。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap">
                <Calendar size={24} strokeWidth={2} />
              </div>
              <h3>十年行业深耕</h3>
              <p>团队专注音乐培训教务系统开发十年，深谙钢琴、吉他、架子鼓、声乐等私教场景。</p>
            </div>
          </div>
        </div>
      </section>

      <section className="music-section" id="music">
        <div className="container">
          <div className="music-header">
            <span className="music-badge">深度适配</span>
            <h2 className="section-title">懂音乐教育，更懂您的管理难题</h2>
            <p className="section-subtitle">
              通用教务系统做不到的，我们原生支持
            </p>
          </div>
          <div className="music-grid">
            <div className="music-card">
              <div className="music-icon">
                <Headphones size={28} />
              </div>
              <h3>一对一智能排课</h3>
              <p>原生适配钢琴、吉他、架子鼓、声乐等一对一私教场景。支持教师、教室、时间三重维度智能匹配。</p>
            </div>
            <div className="music-card">
              <div className="music-icon">
                <Building2 size={28} />
              </div>
              <h3>学员约教室练习</h3>
              <p>学员自主预约琴房/教室进行课后练习，系统自动检测冲突，管理者一键审批，提升教室利用率。</p>
            </div>
            <div className="music-card">
              <div className="music-icon">
                <Users size={28} />
              </div>
              <h3>跨校区约课调度</h3>
              <p>学员可跨校区预约上课，教师可跨校区排课。连锁品牌资源灵活调配，打破校区壁垒。</p>
            </div>
            <div className="music-card">
              <div className="music-icon">
                <Calendar size={28} />
              </div>
              <h3>兼职老师完美适配</h3>
              <p>全职+兼职混合师资体系一键管理。课时费、底薪、提成、业绩分成按机构规则自动结算。</p>
            </div>
            <div className="music-card">
              <div className="music-icon">
                <Lock size={28} />
              </div>
              <h3>加盟校区数据隔离</h3>
              <p>总部统一管理品牌数据，各加盟校区之间数据安全隔离。灵活的权限控制，不同角色各取所需。</p>
            </div>
            <div className="music-card">
              <div className="music-icon">
                <Smartphone size={28} />
              </div>
              <h3>自有品牌小程序</h3>
              <p>使用机构自己的公众号和小程序，学员看到的是您的品牌。支持微信公众号、小程序、企业微信全生态。</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cases-section" id="cases">
        <div className="container">
          <h2 className="section-title">他们都在用申嘻教务</h2>
          <p className="section-subtitle">
            来自音乐连锁机构负责人的真实反馈
          </p>
          <div className="cases-grid">
            <div className="case-card">
              <div className="case-icon">🎸</div>
              <h3>某知名音乐连锁</h3>
              <p className="case-tag">全国连锁音乐培训品牌</p>
              <p className="case-quote">「跨校区排课和教室预约功能彻底解决了我们多校区资源调配的难题，每月节省大量教务人力。」</p>
            </div>
            <div className="case-card">
              <div className="case-icon">🎤</div>
              <h3>某大型音乐培训集团</h3>
              <p className="case-tag">全国音乐教育连锁</p>
              <p className="case-quote">「体验课跟进和续费预警功能让我们的转化率提升了40%，真正做到了每个潜在客户都不遗漏。」</p>
            </div>
            <div className="case-card">
              <div className="case-icon">🎹</div>
              <h3>某声乐教育连锁</h3>
              <p className="case-tag">全国声乐培训连锁</p>
              <p className="case-quote">「工资结算的定制开发完全贴合我们兼职老师多的特点，一键出报表，财务效率提升数倍。」</p>
            </div>
          </div>
        </div>
      </section>

      <section className="compare-section">
        <div className="container">
          <h2 className="section-title">同样花钱，为什么不选一个懂音乐教育的？</h2>
          <p className="section-subtitle">
            这些您每天头疼的问题，我们早就帮您解决了
          </p>
          <div className="compare-table">
            <div className="compare-row compare-header">
              <span className="compare-cell feature">功能场景</span>
              <span className="compare-cell general">通用教务系统</span>
              <span className="compare-cell ours">申嘻教务</span>
            </div>
            <div className="compare-row">
              <span className="compare-cell feature">一对一智能排课</span>
              <span className="compare-cell general">不支持 / 需手动</span>
              <span className="compare-cell ours">原生深度支持</span>
            </div>
            <div className="compare-row">
              <span className="compare-cell feature">学员约教室练习</span>
              <span className="compare-cell general">不支持</span>
              <span className="compare-cell ours">原生支持</span>
            </div>
            <div className="compare-row">
              <span className="compare-cell feature">跨校区约课调度</span>
              <span className="compare-cell general">不支持</span>
              <span className="compare-cell ours">原生支持</span>
            </div>
            <div className="compare-row">
              <span className="compare-cell feature">体验课全流程跟进</span>
              <span className="compare-cell general">基础支持</span>
              <span className="compare-cell ours">完整闭环</span>
            </div>
            <div className="compare-row">
              <span className="compare-cell feature">工资结算定制</span>
              <span className="compare-cell general">固定模板</span>
              <span className="compare-cell ours">按需定制开发</span>
            </div>
            <div className="compare-row">
              <span className="compare-cell feature">加盟校区数据隔离</span>
              <span className="compare-cell general">不支持</span>
              <span className="compare-cell ours">原生支持</span>
            </div>
            <div className="compare-row">
              <span className="compare-cell feature">兼职老师管理</span>
              <span className="compare-cell general">基础支持</span>
              <span className="compare-cell ours">深度适配</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 订阅版本 ============== */}
      <section className="versions-section" id="versions">
        <div className="container">
          <h2 className="section-title">选择适合您的订阅版本</h2>
          <p className="section-subtitle">
            从个人工作室到连锁集团，三档订阅满足不同规模音乐培训机构需求
          </p>

          {versionsLoading && <div className="versions-empty">加载中…</div>}
          {!versionsLoading && versionsError && (
            <div className="versions-empty error">{versionsError}</div>
          )}
          {!versionsLoading && !versionsError && editions.length === 0 && (
            <div className="versions-empty">暂无版本信息</div>
          )}

          {!versionsLoading && !versionsError && editions.length > 0 && (
            <div className="versions-grid">
              {(() => {
                const order = ['BASIC', 'STANDARD', 'FRANCHISE'];
                const editionMap = new Map();
                editions.forEach((ed) => editionMap.set(ed.edition, ed));
                const seen = new Set();
                const hints = {
                  STANDARD: '标准版在基础版基础上还增加以下能力',
                  FRANCHISE: '加盟版在标准版基础上还增加以下能力',
                };
                return order.map((key) => {
                  const ed = editionMap.get(key);
                  if (!ed) return null;
                  const recommended = ed.recommendFlag === 1;
                  const all = ed.capabilities || [];
                  const diff = all.filter((cap) => {
                    const k = cap.id || cap.title;
                    if (!k) return false;
                    if (seen.has(k)) return false;
                    seen.add(k);
                    return true;
                  });
                  return (
                    <div
                      key={ed.id || ed.edition}
                      className={recommended ? 'version-card recommended' : 'version-card'}
                    >
                      {recommended && <div className="ribbon">推荐</div>}
                      <div className="version-name">{ed.displayName}</div>
                      {ed.subtitle && <div className="version-subtitle">{ed.subtitle}</div>}
                      {ed.priceText && <div className="version-price">{ed.priceText}</div>}

                      <ul className="capability-list">
                        {hints[key] && (
                          <li className="capability-hint">{hints[key]}</li>
                        )}
                        {diff.map((cap) => (
                          <li className="capability-item" key={cap.id || cap.title}>
                            <span className="check-icon">
                              <Check size={16} strokeWidth={2.5} />
                            </span>
                            <div className="cap-text">
                              <div className="cap-title">{cap.title}</div>
                              {cap.description && (
                                <div className="cap-desc">{cap.description}</div>
                              )}
                            </div>
                          </li>
                        ))}
                        {diff.length === 0 && (
                          <li className="capability-empty">— 暂无额外能力 —</li>
                        )}
                      </ul>

                      <a href="#contact" className="btn-cta">
                        立即试用
                      </a>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </section>

      <section className="contact" id="contact">
        <div className="container">
          <h2 className="section-title">试用满意后再签约</h2>
          <p className="section-subtitle">
            留下您的信息，我们24小时内联系您安排演示
          </p>
          <div className="contact-content">
            <div className="contact-info">
              <h3>试用3个月，满意再付费</h3>
              <ul className="contact-benefits">
                <li><Check size={18} /> 专为音乐私教培训深度定制</li>
                <li><Check size={18} /> 一对一智能排课、教室预约、跨校区调度</li>
                <li><Check size={18} /> 体验课跟进、续费预警、工资一键结算</li>
                <li><Check size={18} /> 全功能开放，零风险试用</li>
                <li><Check size={18} /> 使用机构自有品牌公众号/小程序</li>
              </ul>
            </div>
            <div className="contact-form-wrapper">
              <div className="contact-form">
                {message && (
                  <div className={`message ${message.type}`} role="alert">
                    {message.text}
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="userName">您的姓名</label>
                    <input
                      type="text"
                      id="userName"
                      name="userName"
                      placeholder="请输入您的姓名"
                      required
                      value={formData.userName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mobile">联系电话</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      placeholder="请输入手机号码"
                      required
                      value={formData.mobile}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="companyName">机构名称</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      placeholder="请输入培训机构名称"
                      required
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn primary"
                    disabled={submitting}
                  >
                    {submitting ? '提交中...' : '立即申请免费试用'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>申嘻教务系统</h3>
              <p>专注音乐培训教务管理十年，深度定制，让您专注教学。</p>
            </div>
            <div className="footer-section">
              <h4>快速链接</h4>
              <ul>
                <li><a href="#features">核心能力</a></li>
                <li><a href="#music">音乐专属</a></li>
                <li><a href="#cases">客户案例</a></li>
                <li><a href="#contact">免费试用</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} 深圳申嘻科技有限公司. 保留所有权利.</p>
            <p className="footer-icp">
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备19098413号-2</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
